import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../contexts/LanguageContext";
import { PublicHeader } from "./PublicPages";
import Footer from "../components/app/Footer";
import SEOHead from "../components/app/SEOHead";
import { ArrowRight, Calendar } from "@phosphor-icons/react";

export default function Blog() {
  const { t, lang } = useT();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/blog?lang=${lang}&limit=30`);
        setPosts(data.posts || []);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
        year: "numeric", month: "short", day: "numeric",
      });
    } catch { return ""; }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead pageKey="blog" />
      <PublicHeader />
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full" data-testid="blog-list-page">
        <div className="overline text-[#002FA7]">{t("blog.overline")}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">{t("blog.title")}</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">{t("blog.subtitle")}</p>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {loading && Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card animate-pulse h-56 bg-slate-100" data-testid={`blog-skeleton-${i}`} />
          ))}
          {!loading && posts.length === 0 && (
            <div className="text-slate-500 col-span-2 py-8">{t("blog.empty")}</div>
          )}
          {!loading && posts.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="card hover:shadow-lg transition-shadow group"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Calendar size={14} weight="bold" />
                {fmtDate(p.published_at)}
                {p.tags?.length > 0 && (
                  <>
                    <span>·</span>
                    <span className="text-[#002FA7] font-medium">{p.tags[0]}</span>
                  </>
                )}
              </div>
              <h2 className="font-display text-xl font-bold mt-3 group-hover:text-[#002FA7] transition-colors leading-snug">
                {lang === "tr" ? p.title_tr : (p.title_en || p.title_tr)}
              </h2>
              <p className="text-sm text-slate-600 mt-3 line-clamp-3 leading-relaxed">
                {lang === "tr" ? p.excerpt_tr : (p.excerpt_en || p.excerpt_tr)}
              </p>
              <div className="mt-4 inline-flex items-center gap-1 text-sm text-[#002FA7] font-semibold">
                {t("blog.read_more")} <ArrowRight size={14} weight="bold" />
              </div>
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
